/**
 * useAutoCompleteShipment
 *
 * Watches the packing state and, when the last item of an eligible shipment has been scanned:
 * 1. prints the packing slip (only when the owner org opted in via NoClickDtcPackingSlip)
 * 2. looks up  a saved ShipmentPackageDefault for the shipment's exact content set.
 * 3. If one is found, shows a cancelable countdown and then runs the finish sequence:
 *   3a. close the carton with the saved box type/weight
 *   3b. complete the pack with the computed carrier/method (labels)
 *   3c. print the label
 *   3d. mark the shipment shipped
 *
 *
 * Eligibility:
 *  - the shipment's owner org is in the No Click DTC beta (NoClickDtcShippingBeta setting)
 *  - a scan happened for this shipment in this session
 *  - scan queue empty and not flushing
 *  - shipment status Input/Scheduled/Picked, every item packed
 *  - exactly one open root carton and no packages yet (or one open legacy package)
 *  - not a pallet/freight shipment, no serialized products
 *  - destination address valid — checked last, after a default is found, so an invalid
 *    address gets an explanatory notifaction
 *
 * Note:
 * Defaults match on content only. Box type and weight always come from the default.
 * Carrier/method is never taken from the saved default: after carton close, the
 * route segment already has the PSSM-computed method (from the close SECA).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { message, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { getPackageDefault } from '../../../redux/packing/packingThunks';
import { selectDestContactMechValid } from '../../../redux/packing/packingSelectors';
import { useNoClickDtcBetaEnabled, useNoClickDtcPackingSlipEnabled } from '../../../utils/betaAccess';
import { flushScanQueueNow } from '../../../redux/packing/scanQueue';
import { AUTO_ELIGIBLE_STATUSES, areAllItemsPacked, resolveAutoCloseTarget } from '../shared/autoCompleteRules';
import { usePackingState } from './usePackingState';
import { usePackingActions } from './usePackingActions';
import { useContainerActions } from './useContainerActions';
import { useLabelPrinting, downloadLabelUrl } from './useLabelPrinting';
var COUNTDOWN_SECONDS = 3;
export var useAutoCompleteShipment = function (options) {
    var _a, _b, _c;
    var t = useTranslation().t;
    var dispatch = useDispatch();
    var _d = usePackingState(), shipmentId = _d.shipmentId, shipmentStatus = _d.shipmentStatus, shipmentPackages = _d.shipmentPackages, shipmentItemDetailList = _d.shipmentItemDetailList, shipmentItemSourceList = _d.shipmentItemSourceList, shipmentContainerList = _d.shipmentContainerList;
    var noClickDtcBetaEnabled = useNoClickDtcBetaEnabled();
    var noClickDtcPackingSlipEnabled = useNoClickDtcPackingSlipEnabled();
    var _e = usePackingActions(), handleMarkPacked = _e.handleMarkPacked, handleMarkShipped = _e.handleMarkShipped, handleClosePackage = _e.handleClosePackage;
    var handleCloseContainer = useContainerActions().handleCloseContainer;
    var _f = useLabelPrinting(), printAllLabels = _f.printAllLabels, printPackingSlip = _f.printPackingSlip;
    var scanQueuePendingCount = useSelector(function (state) { return state.packing.scanQueuePendingCount; });
    var scanQueueFlushing = useSelector(function (state) { return state.packing.scanQueueFlushing; });
    var _g = useState('idle'), phase = _g[0], setPhase = _g[1];
    var _h = useState(COUNTDOWN_SECONDS), secondsLeft = _h[0], setSecondsLeft = _h[1];
    var _j = useState(''), runningStep = _j[0], setRunningStep = _j[1];
    var _k = useState(null), defaults = _k[0], setDefaults = _k[1];
    var _l = useState(null), printFailure = _l[0], setPrintFailure = _l[1];
    // scan activity is per shipment so opening an already-complete shipment never triggers
    var scanActivityShipmentRef = useRef(null);
    // fire at most once per shipment
    var triggeredShipmentRef = useRef(null);
    var notifyScanActivity = useCallback(function () {
        scanActivityShipmentRef.current = shipmentId !== null && shipmentId !== void 0 ? shipmentId : null;
    }, [shipmentId]);
    var allItemsPacked = useMemo(function () { return areAllItemsPacked(shipmentItemSourceList); }, [shipmentItemSourceList]);
    var hasSerialized = useMemo(function () { return !!(shipmentItemDetailList === null || shipmentItemDetailList === void 0 ? void 0 : shipmentItemDetailList.some(function (item) { return (item === null || item === void 0 ? void 0 : item.serialized) === 'Y'; })); }, [shipmentItemDetailList]);
    var resolveCloseTarget = useCallback(function () { return resolveAutoCloseTarget(shipmentContainerList, shipmentPackages); }, [shipmentContainerList, shipmentPackages]);
    // keep the latest values available to the async sequence without re-binding it
    var defaultsRef = useRef(null);
    defaultsRef.current = defaults;
    // for reading the route segment inside the async sequence: containerClose awaits a
    // fetchShipmentDetails, and redux state is updated synchronously by that dispatch, so
    // store.getState() after the close is guaranteed to see any PSSM re-application from
    // the close SECA — unlike selector props or refs, which need a re-render to catch up
    var store = useStore();
    var resetToIdle = useCallback(function () {
        var _a;
        setPhase('idle');
        setDefaults(null);
        setPrintFailure(null);
        (_a = options === null || options === void 0 ? void 0 : options.onRequestScanFocus) === null || _a === void 0 ? void 0 : _a.call(options);
    }, [options]);
    var cancel = useCallback(function () {
        if (phase !== 'countdown')
            return;
        resetToIdle();
    }, [phase, resetToIdle]);
    var finishShipping = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setPhase('running');
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    setRunningStep(t('Marking shipped'));
                    return [4 /*yield*/, handleMarkShipped()];
                case 2:
                    _a.sent();
                    message.success(t('Shipment auto-completed and shipped'));
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    // the failed step's thunk already surfaced its own error toast
                    message.warning(t('Auto-complete stopped — finish packing manually.'));
                    return [3 /*break*/, 4];
                case 4:
                    resetToIdle();
                    return [2 /*return*/];
            }
        });
    }); }, [handleMarkShipped, resetToIdle, t]);
    var runAutoComplete = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var activeDefaults, target, closeParams, segment, carrierAndShipmentMethod, result, hasAlreadyLabelMessage, printOutcome, error_2;
        var _a, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    activeDefaults = defaultsRef.current;
                    target = resolveCloseTarget();
                    if (!shipmentId || !activeDefaults || !target) {
                        resetToIdle();
                        return [2 /*return*/];
                    }
                    setPhase('running');
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 10, , 11]);
                    setRunningStep(t('Closing carton'));
                    return [4 /*yield*/, flushScanQueueNow(shipmentId)];
                case 2:
                    _e.sent();
                    closeParams = {
                        shipmentBoxTypeId: activeDefaults.shipmentBoxTypeId,
                        weight: activeDefaults.weight,
                        weightUomId: activeDefaults.weightUomId || 'WT_lb'
                    };
                    if (!(target.kind === 'container')) return [3 /*break*/, 4];
                    return [4 /*yield*/, handleCloseContainer(__assign({ containerId: target.containerId }, closeParams))];
                case 3:
                    _e.sent();
                    return [3 /*break*/, 6];
                case 4: return [4 /*yield*/, handleClosePackage(__assign({ shipmentPackageSeqId: target.seqId }, closeParams))];
                case 5:
                    _e.sent();
                    _e.label = 6;
                case 6:
                    setRunningStep(t('Generating shipping label'));
                    segment = (_c = (_b = (_a = store.getState().packing.shipmentData) === null || _a === void 0 ? void 0 : _a.shipmentRouteSegmentList) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : null;
                    if (!(segment === null || segment === void 0 ? void 0 : segment.carrierPartyId) || !(segment === null || segment === void 0 ? void 0 : segment.shipmentMethodEnumId)) {
                        // no method => no auto-complete warn user: Store Shipment Method is not configured.
                        message.warning(t('Store Shipment Method is not found. Contact support or finish packing manually.'));
                        resetToIdle();
                        return [2 /*return*/];
                    }
                    carrierAndShipmentMethod = segment.carrierPartyId + ":" + segment.shipmentMethodEnumId;
                    return [4 /*yield*/, handleMarkPacked({
                            carrierAndShipmentMethod: carrierAndShipmentMethod,
                            getLabels: true,
                            getReturnLabels: false,
                            getLocalPrints: true,
                            serialized: false
                        })];
                case 7:
                    result = _e.sent();
                    hasAlreadyLabelMessage = (_d = result === null || result === void 0 ? void 0 : result.messages) === null || _d === void 0 ? void 0 : _d.includes('already has label');
                    setRunningStep(t('Printing shipping label'));
                    if (!!hasAlreadyLabelMessage) return [3 /*break*/, 9];
                    return [4 /*yield*/, printAllLabels(undefined, {
                            context: t('All labels'),
                            silent: true
                        })];
                case 8:
                    printOutcome = _e.sent();
                    if (printOutcome.status !== 'printed') {
                        setPrintFailure(printOutcome);
                        setPhase('print-failed');
                        return [2 /*return*/];
                    }
                    _e.label = 9;
                case 9: return [3 /*break*/, 11];
                case 10:
                    error_2 = _e.sent();
                    message.warning(t('Auto-complete stopped — finish packing manually.'));
                    resetToIdle();
                    return [2 /*return*/];
                case 11: return [4 /*yield*/, finishShipping()];
                case 12:
                    _e.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [
        shipmentId,
        store,
        resolveCloseTarget,
        handleCloseContainer,
        handleClosePackage,
        handleMarkPacked,
        printAllLabels,
        finishShipping,
        resetToIdle,
        t
    ]);
    var retryPrint = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var printOutcome;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (phase !== 'print-failed')
                        return [2 /*return*/];
                    setPhase('running');
                    setRunningStep(t('Printing shipping label'));
                    return [4 /*yield*/, printAllLabels(undefined, {
                            context: t('All labels'),
                            silent: true
                        })];
                case 1:
                    printOutcome = _a.sent();
                    if (printOutcome.status !== 'printed') {
                        setPrintFailure(printOutcome);
                        setPhase('print-failed');
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, finishShipping()];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [phase, printAllLabels, finishShipping, t]);
    var openFailedLabel = useCallback(function () {
        if (printFailure && printFailure.status !== 'printed' && printFailure.labelUrl) {
            downloadLabelUrl(printFailure.labelUrl);
        }
    }, [printFailure]);
    var shipAnyway = useCallback(function () {
        if (phase !== 'print-failed')
            return;
        void finishShipping();
    }, [phase, finishShipping]);
    var dismissPrintFailure = useCallback(function () {
        if (phase !== 'print-failed')
            return;
        message.info(t('Shipment is packed and its label is generated but NOT printed — print it and mark shipped manually.'));
        resetToIdle();
    }, [phase, resetToIdle, t]);
    var printErrorText = useMemo(function () {
        var _a, _b;
        if (!printFailure || printFailure.status === 'printed')
            return '';
        if (printFailure.status === 'skipped') {
            return printFailure.reason === 'qz-disconnected'
                ? t('QZ Tray is not connected')
                : t('No printer is selected');
        }
        var rawError = printFailure.error;
        return String((_b = (_a = rawError === null || rawError === void 0 ? void 0 : rawError.message) !== null && _a !== void 0 ? _a : rawError) !== null && _b !== void 0 ? _b : t('Unknown print error'));
    }, [printFailure, t]);
    // trigger: evaluate after every settled refresh while idle
    useEffect(function () {
        if (!shipmentId || phase !== 'idle')
            return;
        if (triggeredShipmentRef.current === shipmentId)
            return;
        if (!noClickDtcBetaEnabled)
            return;
        if (scanActivityShipmentRef.current !== shipmentId)
            return;
        if (scanQueuePendingCount > 0 || scanQueueFlushing)
            return;
        if (!AUTO_ELIGIBLE_STATUSES.includes(shipmentStatus !== null && shipmentStatus !== void 0 ? shipmentStatus : ''))
            return;
        if (!allItemsPacked)
            return;
        if (hasSerialized)
            return;
        if (!resolveCloseTarget())
            return;
        triggeredShipmentRef.current = shipmentId;
        // the packing slip prints in parallel with the countdown and is wanted whether or not
        // a default exists / the countdown is cancelled, only for orgs that opted in
        if (noClickDtcPackingSlipEnabled) {
            void printPackingSlip(undefined, { context: t('Packing slip') });
        }
        dispatch(getPackageDefault({ shipmentId: shipmentId }))
            .unwrap()
            .then(function (result) {
            if (!(result === null || result === void 0 ? void 0 : result.found) ||
                !result.shipmentBoxTypeId ||
                result.weight == null) {
                return;
            }
            if (!selectDestContactMechValid(store.getState())) {
                notification.warning({
                    key: 'fp2665-address-invalid',
                    message: t('Auto Complete skipped'),
                    description: t('This shipment has a saved auto-complete default, but the destination address has not been validated. Finish packing manually.')
                });
                return;
            }
            setDefaults(result);
            setSecondsLeft(COUNTDOWN_SECONDS);
            setPhase('countdown');
        })
            .catch(function () {
            // lookup failure => no auto-complete
        });
    }, [
        shipmentId,
        phase,
        noClickDtcBetaEnabled,
        noClickDtcPackingSlipEnabled,
        scanQueuePendingCount,
        scanQueueFlushing,
        shipmentStatus,
        allItemsPacked,
        hasSerialized,
        resolveCloseTarget,
        printPackingSlip,
        dispatch,
        store,
        t
    ]);
    // countdown tick
    useEffect(function () {
        if (phase !== 'countdown')
            return;
        if (secondsLeft <= 0) {
            void runAutoComplete();
            return;
        }
        var timerId = setTimeout(function () { return setSecondsLeft(function (prev) { return prev - 1; }); }, 1000);
        return function () { return clearTimeout(timerId); };
    }, [phase, secondsLeft, runAutoComplete]);
    // countdown only previews box type/weight; carrier/method is applied after close (PSSM)
    var target = resolveCloseTarget();
    var cartonLabel = (target === null || target === void 0 ? void 0 : target.kind) === 'package'
        ? t('Package') + " " + target.seqId
        : t('Carton') + " #" + ((_a = target === null || target === void 0 ? void 0 : target.containerSeqNum) !== null && _a !== void 0 ? _a : '1');
    return {
        phase: phase,
        secondsLeft: secondsLeft,
        runningStep: runningStep,
        cartonLabel: cartonLabel,
        boxTypeDescription: (defaults === null || defaults === void 0 ? void 0 : defaults.boxTypeDescription) || (defaults === null || defaults === void 0 ? void 0 : defaults.shipmentBoxTypeId) || '',
        weightText: (((_b = defaults === null || defaults === void 0 ? void 0 : defaults.weight) !== null && _b !== void 0 ? _b : '') + " " + ((defaults === null || defaults === void 0 ? void 0 : defaults.weightUomId) === 'WT_lb' ? 'lbs' : (_c = defaults === null || defaults === void 0 ? void 0 : defaults.weightUomId) !== null && _c !== void 0 ? _c : '')).trim(),
        printErrorText: printErrorText,
        cancel: cancel,
        notifyScanActivity: notifyScanActivity,
        retryPrint: retryPrint,
        openFailedLabel: openFailedLabel,
        shipAnyway: shipAnyway,
        dismissPrintFailure: dismissPrintFailure
    };
};
export default useAutoCompleteShipment;
